import { useCallback } from 'react';
import {
  ExclamationTriangleIcon,
  ForbiddenIcon,
  MoneyIcon,
} from '@nimbus-ds/icons';
import { useNavegate } from 'App/hooks';
import { openWindow } from 'commons/utils/window';
import { useAdminLinks, useIsMobileDevice } from 'domains/Auth/hooks';
import useGetPlanDebt from 'domains/Auth/hooks/useGetPlanDebt';
import { BILLING_ROUTES } from 'domains/Billing/billingRoutes';
import { CHECKOUT_ROUTES } from 'domains/Billing/Checkout/checkoutRoutes';
import { useStoreChurnedStatus } from 'domains/Billing/hooks';
import DisabledStoreContentCommon from '../DisabledStoreContentCommon';
import DisabledStoreOld from '../DisabledStoreOld';

export interface DisabledStoreContentProps {
  handleRecoverStore: () => void;
  logout: () => void;
  isLoadingRecoverStore?: boolean;
}

export default function DisabledStoreContent({
  handleRecoverStore,
  logout,
  isLoadingRecoverStore,
}: Readonly<DisabledStoreContentProps>) {
  const {
    shouldUseDebtData,
    isStoreDisabledManually,
    isAdminDisabled,
    isStoreDisabled,
  } = useGetPlanDebt();
  const { canReactivateWithoutPayment } = useStoreChurnedStatus();

  const { goTo } = useNavegate();
  const isMobileDevice = useIsMobileDevice();
  const { getNewAdminLink } = useAdminLinks();

  const goToRoute = useCallback(
    (route: string) => {
      if (isMobileDevice) {
        openWindow(getNewAdminLink(route), true);
      } else {
        goTo(route);
      }
    },
    [goTo, isMobileDevice, getNewAdminLink],
  );

  if (!shouldUseDebtData) {
    return (
      <DisabledStoreOld
        handleRecoverStore={handleRecoverStore}
        logout={logout}
      />
    );
  }

  if (canReactivateWithoutPayment || isStoreDisabledManually) {
    return (
      <DisabledStoreContentCommon
        localePath="disabledStoreManually"
        iconSource={ForbiddenIcon}
        primaryButtonAction={handleRecoverStore}
        secondaryButtonAction={logout}
        isLoadingPrimaryAction={isLoadingRecoverStore}
      />
    );
  }

  if (isStoreDisabled) {
    return (
      <DisabledStoreContentCommon
        localePath="disabledStore"
        iconSource={ExclamationTriangleIcon}
        primaryButtonAction={handleRecoverStore}
        secondaryButtonAction={() => {
          goToRoute(BILLING_ROUTES.plans);
        }}
        isLoadingPrimaryAction={isLoadingRecoverStore}
      />
    );
  }

  if (isAdminDisabled) {
    return (
      <DisabledStoreContentCommon
        localePath="disabledAdmin"
        iconSource={MoneyIcon}
        primaryButtonAction={() => {
          goToRoute(CHECKOUT_ROUTES.plansAndSubscriptions);
        }}
        secondaryButtonAction={() => {
          goToRoute(BILLING_ROUTES.plans);
        }}
      />
    );
  }

  return (
    <DisabledStoreOld handleRecoverStore={handleRecoverStore} logout={logout} />
  );
}
